import { authFetch } from '../services/apiClient';

const BASE_RECON_URL = 'https://recon.jcinformatics.com/';
const EXPORT_SHEET_NAME = 'Partes';
const SUMMARY_SHEET_NAME = 'Resumen';
const ISSUES_SHEET_NAME = 'Incidencias';

const PHOTO_COLUMN_INDEX = 9;
const PHOTO_COLUMN_WIDTH = 16;
const PHOTO_ROW_HEIGHT = 105;
const PHOTO_TARGET_WIDTH_PX = 90;
const PHOTO_TARGET_HEIGHT_PX = 125;
const PHOTO_CANVAS_SCALE = 2;

// Mantiene la foto en formato vertical.
const PHOTO_FORCE_PORTRAIT = false;

// Da la vuelta a la foto 180 grados.
// Si después te saliera bien sin girar, cambia esto a false.
const PHOTO_ROTATE_180 = false;

const IMAGE_FETCH_TIMEOUT_MS = 20000;
const IMAGE_FETCH_RETRIES = 3;
const IMAGE_FETCH_CONCURRENCY = 4;

const padNumber = (value) => String(value).padStart(2, '0');

const trimString = (value) => (typeof value === 'string' ? value.trim() : '');

const formatDateTimeStamp = (date = new Date()) =>
	`${padNumber(date.getDate())}/${padNumber(date.getMonth() + 1)}/${date.getFullYear()} ${padNumber(
		date.getHours()
	)}:${padNumber(date.getMinutes())}`;

const parseDateParts = (value) => {
	const normalized = trimString(value);

	if (!normalized) {
		return null;
	}

	const isoMatch = normalized.match(
		/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
	);

	if (isoMatch) {
		return {
			year: Number(isoMatch[1]),
			month: Number(isoMatch[2]),
			day: Number(isoMatch[3]),
			hours: Number(isoMatch[4] ?? 0),
			minutes: Number(isoMatch[5] ?? 0),
		};
	}

	const date = new Date(normalized);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return {
		year: date.getFullYear(),
		month: date.getMonth() + 1,
		day: date.getDate(),
		hours: date.getHours(),
		minutes: date.getMinutes(),
	};
};

const formatExcelDate = (value) => {
	const parts = parseDateParts(value);

	if (!parts) {
		return '';
	}

	return `${padNumber(parts.day)}/${padNumber(parts.month)}/${parts.year}`;
};

const formatExcelTime = (value) => {
	const parts = parseDateParts(value);

	if (!parts) {
		return '';
	}

	return `${padNumber(parts.hours)}:${padNumber(parts.minutes)}`;
};

const formatExcelExitDate = (value, fallbackDate) => {
	const directValue = formatExcelDate(value);

	if (directValue) {
		return directValue;
	}

	return formatExcelDate(fallbackDate);
};

const formatExcelExitTime = (value) => {
	const directValue = formatExcelTime(value);

	if (directValue) {
		return directValue;
	}

	return '--:--';
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeCandidateUrl = (value) => {
	const trimmedValue = trimString(value);

	if (!trimmedValue) {
		return '';
	}

	if (/^https?:\/\//i.test(trimmedValue)) {
		return trimmedValue;
	}

	if (trimmedValue.startsWith('/')) {
		try {
			return new URL(trimmedValue, BASE_RECON_URL).toString();
		} catch (error) {
			return '';
		}
	}

	return '';
};

const getRecolectoresForExport = (detalle) => {
	const grupos = detalle?.recolectores ?? {};

	return [
		...(Array.isArray(grupos.comprobados) ? grupos.comprobados : []),
		...(Array.isArray(grupos.error_comprobacion) ? grupos.error_comprobacion : []),
		...(Array.isArray(grupos.no_comprobados) ? grupos.no_comprobados : []),
	];
};

const hasValidFichajeScore = (recolector) => {
	const comprobado = Number(recolector?.comprobado ?? 0);
	const respuesta = Number(recolector?.respuesta_comprobacion ?? 0);
	const puntuacion = Number(recolector?.puntuacion_comprobacion);

	return (
		comprobado === 1 &&
		respuesta === 1 &&
		Number.isFinite(puntuacion) &&
		puntuacion > 0
	);
};

const getRecolectorExportImageCandidates = (recolector) => {
	if (!hasValidFichajeScore(recolector)) {
		return [];
	}

	const candidates = [];

	const pushCandidate = (url, source) => {
		const normalizedUrl = normalizeCandidateUrl(url);

		if (
			normalizedUrl &&
			!candidates.some((candidate) => candidate.url === normalizedUrl)
		) {
			candidates.push({
				url: normalizedUrl,
				source,
			});
		}
	};

	pushCandidate(recolector?.url_imagen_endpoint, 'endpoint');
	pushCandidate(recolector?.url_imagen, 'url_imagen');
	pushCandidate(recolector?.imagen_url, 'imagen_url');
	pushCandidate(recolector?.ruta_imagen, 'ruta_imagen');
	pushCandidate(recolector?.imagen_fichaje, 'imagen_fichaje');

	if (Number(recolector?.id_empleo) > 0) {
		pushCandidate(
			`${BASE_RECON_URL}imagefichaje.php?id_empleo=${Number(recolector.id_empleo)}`,
			'id_empleo'
		);
	}

	return candidates;
};

const getInitialObservation = ({ hasValidScore, imageCandidates }) => {
	if (!hasValidScore) {
		return 'Sin puntuacion valida';
	}

	if (imageCandidates.length === 0) {
		return 'Fichaje valido sin ruta de imagen';
	}

	return '';
};

const buildRowsFromCuadrillaDetail = (detalle, options = {}) => {
	const includeCheckInformativo = options?.includeCheckInformativo === true;
	const datos = detalle?.datos_cuadrilla ?? {};
	const nombreCabo = [datos?.nombre_cabo, datos?.apellidos_cabo]
		.filter(Boolean)
		.join(' ')
		.trim();
	const empresa = String(datos?.nombre_cliente || '');
	const delegacion = String(datos?.nombre_delegacion || '');
	const idCuadrilla = Number(datos?.id_cuadrilla ?? 0) || null;

	return getRecolectoresForExport(detalle).map((recolector) => {
		const trabajador = [recolector?.nombre_recolector, recolector?.apellidos_recolector]
			.filter(Boolean)
			.join(' ')
			.trim();
		const horaEntradaBase = recolector?.fecha_comprobacion || recolector?.fecha_exportado_in || '';
		const hasValidScore = hasValidFichajeScore(recolector);
		const imageCandidates = getRecolectorExportImageCandidates(recolector);
		const latitud = trimString(recolector?.lat);
		const longitud = trimString(recolector?.long);
		const mapaUrl =
			latitud && longitud
				? `https://www.google.com/maps?q=${encodeURIComponent(`${latitud},${longitud}`)}`
				: '';
		const tieneHorario = Boolean(
			recolector?.fecha_comprobacion ||
			recolector?.fecha_exportado_in ||
			recolector?.fecha_exportado_out
		);

		return {
			idCuadrilla,
			empresa,
			delegacion,
			cabo: nombreCabo,
			trabajador,
			checkInformativo: includeCheckInformativo
				? Number(recolector?.check_informativo ?? 0) === 1
					? 'Si'
					: ''
				: undefined,
			nif: String(recolector?.nif_recolector || ''),
			fechaEntrada: formatExcelDate(horaEntradaBase),
			horaEntrada: formatExcelTime(horaEntradaBase),
			fechaSalida: tieneHorario
				? formatExcelExitDate(
					recolector?.fecha_exportado_out,
					datos?.fecha_cuadrilla || horaEntradaBase
				)
				: '',
			horaSalida: tieneHorario
				? formatExcelExitTime(recolector?.fecha_exportado_out)
				: '',
			mapaUrl,
			imageExpected: hasValidScore,
			imageCandidates,
			photoLabel: hasValidScore ? 'Pendiente' : 'No aplica',
			observacion: getInitialObservation({ hasValidScore, imageCandidates }),
		};
	});
};

const buildCacheBustedUrl = (url, attempt) => {
	try {
		const parsed = new URL(url, BASE_RECON_URL);
		parsed.searchParams.set('_excel_export_ts', `${Date.now()}-${attempt}`);
		return parsed.toString();
	} catch (error) {
		const separator = url.includes('?') ? '&' : '?';
		return `${url}${separator}_excel_export_ts=${Date.now()}-${attempt}`;
	}
};

const readNonImageResponseMessage = async (blob) => {
	try {
		const text = (await blob.text()).replace(/\s+/g, ' ').trim();
		return text.slice(0, 180);
	} catch (error) {
		return '';
	}
};

const ensureImageBlob = async (response) => {
	if (!response.ok) {
		throw new Error(`HTTP ${response.status}`);
	}

	const blob = await response.blob();
	if (!blob || blob.size <= 0) {
		throw new Error('Respuesta vacia');
	}

	const contentType = String(response.headers.get('content-type') || blob.type || '').toLowerCase();
	if (
		contentType.includes('application/json') ||
		contentType.includes('text/html') ||
		contentType.includes('text/plain')
	) {
		const message = await readNonImageResponseMessage(blob);
		throw new Error(message || 'La respuesta no contiene una imagen valida');
	}

	return blob;
};

const fetchImageBlobOnce = async (url, attempt) => {
	const controller =
		typeof AbortController === 'function' ? new AbortController() : null;
	const timeoutId =
		controller !== null
			? setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS)
			: null;

	try {
		const response = await authFetch(
			buildCacheBustedUrl(url, attempt),
			{
				cache: 'no-store',
				headers: {
					'Cache-Control': 'no-cache, no-store, must-revalidate',
					Pragma: 'no-cache',
				},
				signal: controller?.signal,
			},
			{ retryStatuses: [401, 403] }
		);

		return ensureImageBlob(response);
	} catch (error) {
		if (error?.name === 'AbortError') {
			throw new Error('Tiempo de espera agotado');
		}

		throw error;
	} finally {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
		}
	}
};

const fetchImageBlobWithRetry = async (url) => {
	let lastError = null;

	for (let attempt = 1; attempt <= IMAGE_FETCH_RETRIES; attempt += 1) {
		try {
			return await fetchImageBlobOnce(url, attempt);
		} catch (error) {
			lastError = error;

			if (attempt < IMAGE_FETCH_RETRIES) {
				await sleep(250 * attempt);
			}
		}
	}

	throw lastError || new Error('No se pudo descargar la imagen');
};

const loadImageElementFromBlob = (blob) =>
	new Promise((resolve, reject) => {
		const objectUrl = URL.createObjectURL(blob);
		const image = new Image();

		image.onload = () => {
			URL.revokeObjectURL(objectUrl);
			resolve(image);
		};

		image.onerror = () => {
			URL.revokeObjectURL(objectUrl);
			reject(new Error('No se pudo decodificar la imagen'));
		};

		image.decoding = 'async';
		image.src = objectUrl;
	});

const renderImageToPngDataUrl = (imageElement) => {
	const sourceWidth = Number(imageElement?.naturalWidth || imageElement?.width || 0);
	const sourceHeight = Number(imageElement?.naturalHeight || imageElement?.height || 0);

	if (!sourceWidth || !sourceHeight) {
		throw new Error('La imagen no tiene dimensiones validas');
	}

	const canvas = document.createElement('canvas');
	canvas.width = PHOTO_TARGET_WIDTH_PX * PHOTO_CANVAS_SCALE;
	canvas.height = PHOTO_TARGET_HEIGHT_PX * PHOTO_CANVAS_SCALE;

	const context = canvas.getContext('2d');
	if (!context) {
		throw new Error('No se pudo crear el contexto del canvas');
	}

	context.fillStyle = '#ffffff';
	context.fillRect(0, 0, canvas.width, canvas.height);
	context.imageSmoothingEnabled = true;
	context.imageSmoothingQuality = 'high';

	const forcePortraitRotation = PHOTO_FORCE_PORTRAIT && sourceWidth > sourceHeight;
	const finalWidth = forcePortraitRotation ? sourceHeight : sourceWidth;
	const finalHeight = forcePortraitRotation ? sourceWidth : sourceHeight;

	const scale = Math.min(canvas.width / finalWidth, canvas.height / finalHeight);
	const drawWidth = sourceWidth * scale;
	const drawHeight = sourceHeight * scale;

	context.save();
	context.translate(canvas.width / 2, canvas.height / 2);

	if (forcePortraitRotation) {
		context.rotate(0.5 * Math.PI);
	}

	// Corrección para las fotos que salen del revés.
	if (PHOTO_ROTATE_180) {
		context.rotate(Math.PI);
	}

	context.drawImage(
		imageElement,
		-drawWidth / 2,
		-drawHeight / 2,
		drawWidth,
		drawHeight
	);

	context.restore();

	return canvas.toDataURL('image/png');
};

const buildEmbeddedImagePayload = async (blob) => {
	if (typeof document === 'undefined') {
		throw new Error('La exportacion con imagenes requiere entorno de navegador');
	}

	const imageElement = await loadImageElementFromBlob(blob);

	return {
		base64: renderImageToPngDataUrl(imageElement),
		extension: 'png',
	};
};

const resolveCandidateImage = async (candidate) => {
	const blob = await fetchImageBlobWithRetry(candidate.url);
	const imagePayload = await buildEmbeddedImagePayload(blob);

	return {
		status: 'embedded',
		imagePayload,
		source: candidate.source,
		url: candidate.url,
	};
};

const resolveEmbeddedImageForCandidates = async (candidates) => {
	const errors = [];

	for (const candidate of candidates) {
		try {
			return await resolveCandidateImage(candidate);
		} catch (error) {
			errors.push({
				source: candidate.source,
				url: candidate.url,
				message: error?.message || 'Error desconocido',
			});
		}
	}

	return {
		status: 'failed',
		errors,
	};
};

const mapWithConcurrency = async (items, worker, limit = IMAGE_FETCH_CONCURRENCY) => {
	const results = new Array(items.length);
	let nextIndex = 0;

	const runWorker = async () => {
		while (nextIndex < items.length) {
			const currentIndex = nextIndex;
			nextIndex += 1;
			results[currentIndex] = await worker(items[currentIndex], currentIndex);
		}
	};

	const workerCount = Math.max(1, Math.min(limit, items.length));
	await Promise.all(Array.from({ length: workerCount }, runWorker));

	return results;
};

const hydrateRowsWithImages = async (rows) => {
	const imageCache = new Map();

	return mapWithConcurrency(rows, async (row) => {
		if (!row.imageExpected) {
			return {
				...row,
				photoStatus: 'not_required',
				photoLabel: 'No aplica',
				issueDetail: '',
				imagePayload: null,
			};
		}

		if (!Array.isArray(row.imageCandidates) || row.imageCandidates.length === 0) {
			return {
				...row,
				photoStatus: 'missing_source',
				photoLabel: 'Sin ruta',
				observacion: 'Fichaje valido sin ruta de imagen',
				issueDetail: 'El detalle del parte no devolvio ninguna URL utilizable para la foto.',
				imagePayload: null,
			};
		}

		const cacheKey = row.imageCandidates.map((candidate) => candidate.url).join('|');
		if (!imageCache.has(cacheKey)) {
			imageCache.set(cacheKey, resolveEmbeddedImageForCandidates(row.imageCandidates));
		}

		const resolvedImage = await imageCache.get(cacheKey);
		if (resolvedImage?.status === 'embedded' && resolvedImage.imagePayload?.base64) {
			return {
				...row,
				photoStatus: 'embedded',
				photoLabel: '',
				observacion: '',
				issueDetail: '',
				imagePayload: resolvedImage.imagePayload,
				imageSource: resolvedImage.source,
			};
		}

		const firstError = resolvedImage?.errors?.[0];
		const readableError = firstError?.message ? String(firstError.message).slice(0, 180) : '';

		return {
			...row,
			photoStatus: 'failed',
			photoLabel: 'No incrustada',
			observacion: 'No se pudo incrustar la imagen del fichaje',
			issueDetail: readableError || 'No se pudo descargar o procesar la foto del fichaje.',
			imagePayload: null,
		};
	});
};

const getPhotoCellFill = (photoStatus) => {
	if (photoStatus === 'failed' || photoStatus === 'missing_source') {
		return {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFFDECEC' },
		};
	}

	if (photoStatus === 'not_required') {
		return {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFF5F5F5' },
		};
	}

	return undefined;
};

const applyBaseCellStyles = (
	rowRef,
	photoCellColumnNumber = PHOTO_COLUMN_INDEX + 1
) => {
	rowRef.eachCell((cell) => {
		cell.alignment = {
			vertical: 'middle',
			horizontal: cell.col === photoCellColumnNumber ? 'center' : 'left',
			wrapText: true,
		};
		cell.border = {
			top: { style: 'thin', color: { argb: 'FFD8DEE6' } },
			left: { style: 'thin', color: { argb: 'FFD8DEE6' } },
			bottom: { style: 'thin', color: { argb: 'FFD8DEE6' } },
			right: { style: 'thin', color: { argb: 'FFD8DEE6' } },
		};
		cell.font = {
			name: 'Calibri',
			size: 11,
		};
	});
};
const sanitizeWorksheetName = (value) => {
	const cleaned = String(value || '')
		.replace(/[\[\]\:\*\?\/\\]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

	return cleaned || 'Parte';
};

const truncateWorksheetName = (value) => {
	const cleaned = sanitizeWorksheetName(value);
	return cleaned.length > 31 ? cleaned.slice(0, 31).trim() : cleaned;
};

const makeUniqueWorksheetName = (baseName, usedNames) => {
	const cleanBaseName = truncateWorksheetName(baseName);
	let worksheetName = cleanBaseName;
	let counter = 2;

	while (usedNames.has(worksheetName.toLowerCase())) {
		const suffix = ` ${counter}`;
		const maxBaseLength = 31 - suffix.length;
		worksheetName = `${cleanBaseName.slice(0, maxBaseLength).trim()}${suffix}`;
		counter += 1;
	}

	usedNames.add(worksheetName.toLowerCase());
	return worksheetName;
};

const excelColumnNumberToName = (columnNumber) => {
	let result = '';
	let current = Number(columnNumber);

	while (current > 0) {
		const remainder = (current - 1) % 26;
		result = String.fromCharCode(65 + remainder) + result;
		current = Math.floor((current - 1) / 26);
	}

	return result || 'A';
};

const buildParteWorksheetName = (detalle, index, usedNames) => {
	const datos = detalle?.datos_cuadrilla ?? {};
	const idCuadrilla = Number(datos?.id_cuadrilla ?? 0) || index + 1;
	const cliente = String(datos?.nombre_cliente || '').trim();

	const baseName = cliente
		? `Parte ${idCuadrilla} ${cliente}`
		: `Parte ${idCuadrilla}`;

	return makeUniqueWorksheetName(baseName, usedNames);
};
const buildPartesWorksheet = (
	workbook,
	rows,
	sheetName = EXPORT_SHEET_NAME,
	options = {}
) => {
	const includeCheckInformativo = options?.includeCheckInformativo === true;
	const worksheet = workbook.addWorksheet(sheetName, {
		views: [{ state: 'frozen', ySplit: 1 }],
	});

	worksheet.columns = [
		{ header: 'EMPRESA', key: 'empresa', width: 24 },
		{ header: 'DELEGACION', key: 'delegacion', width: 20 },
		{ header: 'CABO', key: 'cabo', width: 24 },
		{ header: 'TRABAJADOR', key: 'trabajador', width: 30 },
		...(includeCheckInformativo
			? [{ header: 'CHECK', key: 'checkInformativo', width: 12 }]
			: []),
		{ header: 'NIF/NIE', key: 'nif', width: 16 },
		{ header: 'FECHA ENTRADA', key: 'fechaEntrada', width: 16 },
		{ header: 'HORA ENTRADA', key: 'horaEntrada', width: 14 },
		{ header: 'FECHA SALIDA', key: 'fechaSalida', width: 16 },
		{ header: 'HORA SALIDA', key: 'horaSalida', width: 14 },
		{ header: 'FOTO FICHADA', key: 'foto', width: PHOTO_COLUMN_WIDTH },
		{ header: 'OBSERVACIONES', key: 'observacion', width: 38 },
		{ header: 'UBICACION', key: 'ubicacion', width: 18 },
	];

	worksheet.autoFilter = `A1:${excelColumnNumberToName(worksheet.columns.length)}1`;
	worksheet.getRow(1).height = 24;

	worksheet.getRow(1).eachCell((cell) => {
		cell.font = {
			bold: true,
			color: { argb: '000000' },
			name: 'Calibri',
			size: 11,
		};
		cell.fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'a9d08e' },
		};
		cell.alignment = {
			horizontal: 'center',
			vertical: 'middle',
		};
		cell.border = {
			top: { style: 'thin', color: { argb: 'FF16344F' } },
			left: { style: 'thin', color: { argb: 'FF16344F' } },
			bottom: { style: 'thin', color: { argb: 'FF16344F' } },
			right: { style: 'thin', color: { argb: 'FF16344F' } },
		};
	});

	const photoColumnIndex = worksheet.columns.findIndex((column) => column.key === 'foto');
	const mapColumnIndex = worksheet.columns.findIndex((column) => column.key === 'ubicacion');
	const photoCellColumnNumber = photoColumnIndex + 1;
	const mapCellColumnNumber = mapColumnIndex + 1;

	// Primero se crean todas las filas y después se insertan las fotos.
	// Esto evita que Excel genere una línea vacía solo con la imagen.
	const rowsWithRefs = rows.map((row) => {
		const rowRef = worksheet.addRow({
			empresa: row.empresa,
			delegacion: row.delegacion,
			cabo: row.cabo,
			trabajador: row.trabajador,
			...(includeCheckInformativo ? { checkInformativo: row.checkInformativo || '' } : {}),
			nif: row.nif,
			fechaEntrada: row.fechaEntrada,
			horaEntrada: row.horaEntrada,
			fechaSalida: row.fechaSalida,
			horaSalida: row.horaSalida,
			foto: row.photoLabel,
			observacion: row.observacion,
			ubicacion: row.mapaUrl ? 'Ver mapa' : '',
		});

		rowRef.height = PHOTO_ROW_HEIGHT;
		applyBaseCellStyles(rowRef, photoCellColumnNumber);

		const photoCell = rowRef.getCell(photoCellColumnNumber);
		const photoFill = getPhotoCellFill(row.photoStatus);

		if (photoFill) {
			photoCell.fill = photoFill;
		}

		if (row.mapaUrl) {
			const mapCell = rowRef.getCell(mapCellColumnNumber);
			mapCell.value = {
				text: 'Ver mapa',
				hyperlink: row.mapaUrl,
			};
			mapCell.font = {
				name: 'Calibri',
				size: 11,
				color: { argb: 'FF0563C1' },
				underline: true,
			};
		}

		return { row, rowRef };
	});

	rowsWithRefs.forEach(({ row, rowRef }) => {
		if (!row.imagePayload?.base64) {
			return;
		}

		const imageId = workbook.addImage({
			base64: row.imagePayload.base64,
			extension: row.imagePayload.extension,
		});

		worksheet.addImage(imageId, {
			tl: {
				col: photoColumnIndex + 0.2,
				row: rowRef.number - 1 + 0.08,
			},
			ext: {
				width: PHOTO_TARGET_WIDTH_PX,
				height: PHOTO_TARGET_HEIGHT_PX,
			},
			editAs: 'oneCell',
		});
	});

	return worksheet;
};

const buildSummaryWorksheet = (workbook, stats, meta) => {
	const worksheet = workbook.addWorksheet(SUMMARY_SHEET_NAME);
	const rows = [
		['Exportado el', formatDateTimeStamp(meta.generatedAt)],
		['Rango', `${meta.fechaDesde || '-'} a ${meta.fechaHasta || '-'}`],
		['Partes exportados', stats.totalPartes],
		['Partes omitidos', meta.omittedPartes],
		['Trabajadores exportados', stats.totalRows],
		['Fichajes con foto esperada', stats.rowsWithExpectedImage],
		['Fotos incrustadas', stats.rowsWithEmbeddedImage],
		['Sin puntuacion valida', stats.rowsWithoutValidScore],
		['Fichajes validos sin ruta', stats.rowsWithMissingSource],
		['Errores al incrustar foto', stats.rowsWithFailedImage],
	];

	rows.forEach((row) => worksheet.addRow(row));

	worksheet.columns = [
		{ width: 28 },
		{ width: 24 },
	];

	worksheet.getColumn(2).alignment = {
		horizontal: 'left',
		vertical: 'middle',
	};

	worksheet.eachRow((row, rowNumber) => {
		row.eachCell((cell) => {
			cell.border = {
				top: { style: 'thin', color: { argb: 'FFD8DEE6' } },
				left: { style: 'thin', color: { argb: 'FFD8DEE6' } },
				bottom: { style: 'thin', color: { argb: 'FFD8DEE6' } },
				right: { style: 'thin', color: { argb: 'FFD8DEE6' } },
			};
			cell.font = {
				name: 'Calibri',
				size: 11,
				bold: rowNumber === 1 || cell.col === 1,
			};
			cell.fill =
				rowNumber === 1
					? {
						type: 'pattern',
						pattern: 'solid',
						fgColor: { argb: 'FFEAF2F8' },
					}
					: undefined;
		});
	});
};

const buildIssuesWorksheet = (workbook, rows) => {
	const issueRows = rows.filter(
		(row) => row.photoStatus === 'missing_source' || row.photoStatus === 'failed'
	);

	if (issueRows.length === 0) {
		return null;
	}

	const worksheet = workbook.addWorksheet(ISSUES_SHEET_NAME);
	worksheet.columns = [
		{ header: 'PARTE', key: 'idCuadrilla', width: 12 },
		{ header: 'EMPRESA', key: 'empresa', width: 24 },
		{ header: 'TRABAJADOR', key: 'trabajador', width: 30 },
		{ header: 'ESTADO', key: 'estado', width: 18 },
		{ header: 'DETALLE', key: 'detalle', width: 80 },
	];

	worksheet.getRow(1).eachCell((cell) => {
		cell.font = {
			bold: true,
			color: { argb: 'FFFFFFFF' },
			name: 'Calibri',
			size: 11,
		};
		cell.fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FF7A1F1F' },
		};
		cell.alignment = {
			horizontal: 'center',
			vertical: 'middle',
		};
	});

	issueRows.forEach((row) => {
		const label =
			row.photoStatus === 'missing_source' ? 'Sin ruta de imagen' : 'Error al incrustar';
		const issueRow = worksheet.addRow({
			idCuadrilla: row.idCuadrilla || '',
			empresa: row.empresa,
			trabajador: row.trabajador,
			estado: label,
			detalle: row.issueDetail || row.observacion,
		});

		issueRow.eachCell((cell) => {
			cell.alignment = {
				vertical: 'middle',
				wrapText: true,
			};
			cell.border = {
				top: { style: 'thin', color: { argb: 'FFD8DEE6' } },
				left: { style: 'thin', color: { argb: 'FFD8DEE6' } },
				bottom: { style: 'thin', color: { argb: 'FFD8DEE6' } },
				right: { style: 'thin', color: { argb: 'FFD8DEE6' } },
			};
		});
	});

	return worksheet;
};

const buildStats = (rows, totalPartes) =>
	rows.reduce(
		(accumulator, row) => {
			accumulator.totalRows += 1;

			if (row.imageExpected) {
				accumulator.rowsWithExpectedImage += 1;
			} else {
				accumulator.rowsWithoutValidScore += 1;
			}

			if (row.photoStatus === 'embedded') {
				accumulator.rowsWithEmbeddedImage += 1;
			}

			if (row.photoStatus === 'missing_source') {
				accumulator.rowsWithMissingSource += 1;
			}

			if (row.photoStatus === 'failed') {
				accumulator.rowsWithFailedImage += 1;
			}

			return accumulator;
		},
		{
			totalPartes,
			totalRows: 0,
			rowsWithExpectedImage: 0,
			rowsWithEmbeddedImage: 0,
			rowsWithoutValidScore: 0,
			rowsWithMissingSource: 0,
			rowsWithFailedImage: 0,
		}
	);

export const createPartesGrupoExcelWorkbook = async ({
	detalles = [],
	fechaDesde = '',
	fechaHasta = '',
	omittedPartes = 0,
	includeCheckInformativo = false,
	includeAllRows = false,
} = {}) => {
	const detallesArray = Array.isArray(detalles) ? detalles : [];

	const partes = detallesArray.map((detalle, index) => ({
		detalle,
		index,
		rows: buildRowsFromCuadrillaDetail(detalle, {
			includeCheckInformativo,
		}),
	}));

	const allRows = partes.flatMap((parte) => parte.rows);
	const hydratedRows = await hydrateRowsWithImages(allRows);
	const exportedRows = includeAllRows
		? hydratedRows
		: hydratedRows.filter((row) => row.photoStatus === 'embedded');

	let cursor = 0;
	const hydratedPartes = partes
		.map((parte) => {
			const parteRows = hydratedRows.slice(cursor, cursor + parte.rows.length);
			cursor += parte.rows.length;

			return {
				...parte,
				rows: includeAllRows
					? parteRows
					: parteRows.filter((row) => row.photoStatus === 'embedded'),
			};
		})
		.filter((parte) => parte.rows.length > 0);

	const stats = buildStats(exportedRows, hydratedPartes.length);

	const excelJsModule = await import('exceljs');
	const ExcelJS = excelJsModule.default || excelJsModule;
	const workbook = new ExcelJS.Workbook();

	workbook.creator = 'Plana ETT';
	workbook.lastModifiedBy = 'Plana ETT';
	workbook.created = new Date();
	workbook.modified = new Date();
	workbook.calcProperties.fullCalcOnLoad = true;

	const usedSheetNames = new Set();

	hydratedPartes.forEach((parte, index) => {
		const sheetName = buildParteWorksheetName(parte.detalle, index, usedSheetNames);
		buildPartesWorksheet(workbook, parte.rows, sheetName, {
			includeCheckInformativo,
		});
	});

	buildSummaryWorksheet(workbook, stats, {
		fechaDesde,
		fechaHasta,
		omittedPartes,
		generatedAt: new Date(),
	});

	buildIssuesWorksheet(workbook, exportedRows);

	return {
		workbook,
		stats,
		rows: exportedRows,
	};
};
